import { useState, useEffect, useMemo } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, ShoppingBag, Package, Wallet, Network, BarChart3, MapPin, Settings,
  ChefHat, LogOut, Menu as MenuIcon, X, Store, MessageCircle, Users, Undo2
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import LojaHeader from "../components/loja/LojaHeader";
import NetworkDrawer from "../components/loja/NetworkDrawer";
import BackgroundCheckBanner from "../components/BackgroundCheckBanner";
import useOnboardingTour from "../hooks/useOnboardingTour";
import useUnreadChats from "../hooks/useUnreadChats";
import { LOJISTA_STEPS } from "../components/onboardingSteps";
import { API } from "../App";

const NAV = [
  { to: "/loja/painel", label: "Painel", icon: LayoutDashboard, testId: "loja-nav-dashboard" },
  { to: "/loja/cozinha", label: "Cozinha", icon: ChefHat, testId: "loja-nav-kitchen" },
  { to: "/loja/pedidos", label: "Pedidos", icon: ShoppingBag, testId: "loja-nav-orders", badge: "pendingOrdersCount" },
  { to: "/loja/devolucoes", label: "Devoluções", icon: Undo2, testId: "loja-nav-returns", badge: "pendingReturnsCount" },
  { to: "/loja/produtos", label: "Produtos", icon: Package, testId: "loja-nav-products" },
  { to: "/lojista/chat-pro", label: "Chat", icon: MessageCircle, testId: "loja-nav-chat", badge: "unreadChats", badgeClass: "bg-[#25D366] shadow-[0_0_8px_rgba(37,211,102,0.5)]" },
  { to: "/loja/franquia", label: "Franquia", icon: MessageCircle, testId: "loja-nav-franchise-chat", badge: "unreadFranchise", badgeClass: "bg-[#25D366] shadow-[0_0_8px_rgba(37,211,102,0.5)]" },
  { to: "/loja/financeiro", label: "Financeiro", icon: Wallet, testId: "loja-nav-wallet" },
  { to: "/loja/analytics", label: "Analytics", icon: BarChart3, testId: "loja-nav-analytics" },
  { to: "/loja/mapa", label: "Mapa", icon: MapPin, testId: "loja-nav-map" },
  { to: "/loja/minha-loja", label: "Minha loja", icon: Store, testId: "loja-nav-store" },
  { to: "/loja/grupos", label: "Nossos grupos", icon: Users, testId: "loja-nav-grupos" },
  { to: "/loja/configuracoes", label: "Ajustes", icon: Settings, testId: "loja-nav-settings" },
];

/**
 * Premium dark shell for the Loja (Merchant) module.
 * Replaces the legacy ModuleLayout. Includes header + sidebar + network drawer + main scroll area.
 */
export default function LojaShell({ user, logout, children, badges = {} }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [networkOpen, setNetworkOpen] = useState(false);
  const [returnsSummary, setReturnsSummary] = useState({ open_returns: 0, pending_cancellations: 0 });
  const unread = useUnreadChats();

  // Persist sidebar state
  useEffect(() => {
    const v = localStorage.getItem("loja_sidebar_collapsed");
    if (v === "1") setCollapsed(true);
  }, []);

  // Poll merchant returns summary a cada 30s pra alimentar a badge "Devoluções"
  useEffect(() => {
    let cancelled = false;
    async function fetchSummary() {
      try {
        const token = localStorage.getItem("token");
        if (!token) return;
        const res = await fetch(`${API}/merchant/returns/summary`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled && res.ok) {
          const data = await res.json();
          setReturnsSummary(data);
        }
      } catch (_e) {
        // falha de rede: badge some — melhor que crashar a shell
        void _e;
      }
    }
    fetchSummary();
    const id = setInterval(fetchSummary, 30000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  // Mescla badges externos com o que a shell descobriu sozinha
  const mergedBadges = useMemo(() => ({
    ...badges,
    pendingReturnsCount: (returnsSummary.open_returns || 0) + (returnsSummary.pending_cancellations || 0),
    unreadChats: (unread.pro || 0) + (unread.direct || 0) + (unread.orders || 0),
    unreadFranchise: unread.franchise || 0,
  }), [badges, returnsSummary, unread]);
  const toggleCollapsed = () => {
    setCollapsed((c) => {
      try { localStorage.setItem("loja_sidebar_collapsed", c ? "0" : "1"); } catch (_e) { void _e; }
      return !c;
    });
  };

  const isActive = (to) => location.pathname === to || location.pathname.startsWith(to + "/");

  const handleLogout = () => { logout(); navigate("/login"); };

  return (
    <div className="min-h-screen bg-[#0B0D0F] text-white" data-testid="loja-shell">
      {/* Sidebar (desktop) */}
      <aside
        className={`fixed inset-y-0 left-0 z-30 bg-[#13161A] border-r border-white/5 hidden md:flex flex-col transition-all duration-200 ${collapsed ? "w-16" : "w-60"}`}
        data-testid="loja-sidebar"
      >
        <div className="h-16 px-4 flex items-center justify-between border-b border-white/5">
          {!collapsed && (
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-[#00A86B] to-[#00D68F] flex items-center justify-center shadow-[0_0_16px_rgba(0,214,143,0.4)]">
                <Store className="w-4 h-4 text-white" />
              </div>
              <span className="font-extrabold tracking-tight">GoFood</span>
            </div>
          )}
          <button
            onClick={toggleCollapsed}
            className="w-8 h-8 rounded-lg hover:bg-white/5 flex items-center justify-center"
            data-testid="loja-sidebar-collapse"
            aria-label="Recolher sidebar"
          >
            <MenuIcon className="w-4 h-4 text-white/60" />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-1">
          {NAV.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.to);
            const badgeValue = item.badge ? mergedBadges[item.badge] : 0;
            return (
              <Link
                key={item.to}
                to={item.to}
                className={`relative flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-bold transition-all ${
                  active
                    ? "text-white bg-[#00A86B]/15 shadow-[inset_0_0_0_1px_rgba(0,214,143,0.35),0_0_24px_rgba(0,214,143,0.18)]"
                    : "text-white/60 hover:text-white hover:bg-white/5"
                }`}
                data-testid={item.testId}
                title={collapsed ? item.label : undefined}
              >
                {active && (
                  <motion.div
                    layoutId="loja-nav-pill"
                    className="absolute left-0 top-1.5 bottom-1.5 w-1 rounded-r-full bg-[#00D68F] shadow-[0_0_12px_rgba(0,214,143,0.7)]"
                  />
                )}
                <Icon className={`w-4 h-4 shrink-0 ${active ? "text-[#00D68F]" : ""}`} />
                {!collapsed && <span className="flex-1">{item.label}</span>}
                {!collapsed && badgeValue > 0 && (
                  <span className={`${item.badgeClass || "bg-[#FF6B00]"} text-white text-[10px] font-extrabold px-1.5 rounded-full min-w-[18px] text-center`} data-testid={`${item.testId}-badge`}>
                    {badgeValue > 99 ? "99+" : badgeValue}
                  </span>
                )}
              </Link>
            );
          })}
        </nav>

        <div className="px-2 pb-3 border-t border-white/5 pt-3">
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-bold text-white/60 hover:text-[#FF4D4F] hover:bg-[#FF4D4F]/10"
            data-testid="loja-logout"
          >
            <LogOut className="w-4 h-4 shrink-0" />
            {!collapsed && <span>Sair</span>}
          </button>
        </div>
      </aside>

      {/* Mobile menu drawer */}
      <AnimatePresence>
        {mobileOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-40 bg-black/60 md:hidden"
              onClick={() => setMobileOpen(false)}
            />
            <motion.aside
              initial={{ x: "-100%" }}
              animate={{ x: 0 }}
              exit={{ x: "-100%" }}
              transition={{ type: "spring", stiffness: 360, damping: 30 }}
              className="fixed inset-y-0 left-0 z-50 w-64 bg-[#13161A] border-r border-white/5 md:hidden"
              data-testid="loja-mobile-menu"
            >
              <div className="h-16 px-4 flex items-center justify-between border-b border-white/5">
                <span className="font-extrabold">GoFood</span>
                <button onClick={() => setMobileOpen(false)} className="w-8 h-8 rounded-lg hover:bg-white/5 flex items-center justify-center">
                  <X className="w-4 h-4 text-white/60" />
                </button>
              </div>
              <nav className="py-3 px-2 space-y-1">
                {NAV.map((item) => {
                  const Icon = item.icon;
                  const active = isActive(item.to);
                  const badgeValue = item.badge ? mergedBadges[item.badge] : 0;
                  return (
                    <Link
                      key={item.to}
                      to={item.to}
                      onClick={() => setMobileOpen(false)}
                      className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-bold ${active ? "text-white bg-[#00A86B]/15" : "text-white/60 hover:text-white hover:bg-white/5"}`}
                    >
                      <Icon className="w-4 h-4" />
                      <span className="flex-1">{item.label}</span>
                      {badgeValue > 0 && (
                        <span className={`${item.badgeClass || "bg-[#FF6B00]"} text-white text-[10px] font-extrabold px-1.5 rounded-full min-w-[18px] text-center`}>
                          {badgeValue > 99 ? "99+" : badgeValue}
                        </span>
                      )}
                    </Link>
                  );
                })}
                <button onClick={handleLogout} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-bold text-[#FF4D4F]">
                  <LogOut className="w-4 h-4" /> Sair
                </button>
              </nav>
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* Main area */}
      <div className={`min-h-screen flex flex-col transition-all ${collapsed ? "md:pl-16" : "md:pl-60"}`}>
        <LojaHeader
          user={user}
          onMobileMenu={() => setMobileOpen(true)}
          onOpenNetwork={() => setNetworkOpen(true)}
          badges={mergedBadges}
        />
        <BackgroundCheckBanner user={user} />
        <main className="flex-1 p-4 md:p-6 max-w-7xl w-full mx-auto" data-testid="loja-main">
          {children}
        </main>
      </div>

      {/* Network drawer */}
      <NetworkDrawer open={networkOpen} onClose={() => setNetworkOpen(false)} user={user} />
      {useOnboardingTour({ user, apiBase: API, steps: LOJISTA_STEPS, brandColor: "#00A86B" })}
    </div>
  );
}
